/**
 * Payroll arithmetic, in integer **tetri**.
 *
 * This is the core of the Salaries surface. It is pure, has no knowledge of the
 * store or of journal entries, and every value it touches is an integer number
 * of tetri (1 GEL = 100 tetri) — there is no floating-point money anywhere in it.
 *
 * ## The rule it implements
 *
 * **Gross salary is the expense. Withholdings are liabilities carved *out of*
 * gross, never additional expenses.**
 *
 * For one employee at gross *G*:
 *
 * | Amount              | Value          | What it is                                |
 * | ------------------- | -------------- | ----------------------------------------- |
 * | income tax          | 20% of *G*     | **withheld** — a liability, inside *G*     |
 * | employee pension    | 2% of *G*      | **withheld** — a liability, inside *G*     |
 * | employer pension    | 2% of *G*      | a genuine **extra expense**, on top of *G* |
 * | net                 | *G* − tax − ee | what the employee receives                 |
 *
 * so the entry debits only *G* and the employer's 2%, and credits the two
 * withholdings, the employer's 2% and the net. At ₾3,000: tax ₾600, employee
 * pension ₾60, employer pension ₾60, net ₾2,340, entry ₾3,060 a side.
 *
 * The state's own contribution (up to 2%) goes from the government straight to
 * the employee's pension account. It is computed here for display and is
 * **excluded from every total that feeds the entry** — see
 * {@link PayrollComputation.stateContributionTetri}.
 *
 * ## Why it never drifts
 *
 * Two rules, and they are the whole reason the preview and the draft agree to
 * the tetri however many employees a run covers:
 *
 * 1. **Round once, per employee, per component.** Each percentage is applied to
 *    that employee's own gross and rounded there. A total is then only ever the
 *    *sum of already-rounded per-employee amounts* — a percentage is never
 *    re-applied to a total, which is the classic way a column footer ends up a
 *    tetri away from its rows.
 * 2. **Net is derived from the rounded components**, `gross − tax − employee
 *    pension`, not computed as `78% of gross`. That makes each line balance by
 *    construction:
 *
 *        debits  = gross + employerPension
 *        credits = tax + (employeePension + employerPension) + net
 *                = tax + employeePension + employerPension
 *                  + (gross − tax − employeePension)
 *                = gross + employerPension                       ✓
 *
 *    A run is a sum of per-employee balanced lines, so a run is balanced. There
 *    is no rounding remainder to absorb anywhere.
 *
 * ## Proration
 *
 * An employee hired or terminated mid-period is charged **by calendar days**:
 * `gross × daysEmployed / daysInPeriod`, rounded once, and every percentage is
 * then taken on that prorated gross. Employed for the whole period, the
 * arithmetic is exactly the contractual gross (`round(G × n/n) === G`).
 *
 * OPEN QUESTION: the calendar-day convention is what the brief specifies, but
 * working days (or a full-month convention like the asset register's) is equally
 * defensible and must be confirmed with the accountants. It is stated on the run
 * dialog so it is never a hidden assumption.
 */

import type { PayrollLineReason, PayrollPeriod } from '@/api/models';
import { PAYROLL_RATES, type PayrollRates } from './payrollRates';

// ── money + period helpers ──────────────────────────────────────────────────

/** Integer tetri → a plain 2-dp decimal string (`234000` → `"2340.00"`). */
export const tetriToDecimal = (tetri: number): string => {
    const sign = tetri < 0 ? '-' : '';
    const abs = Math.abs(Math.round(tetri));
    return `${sign}${Math.floor(abs / 100)}.${(abs % 100)
        .toString()
        .padStart(2, '0')}`;
};

/**
 * `bps` basis points of `tetri`, rounded to the nearest tetri, half away from
 * zero. Applied to one employee's gross and never to a column total.
 */
export const applyBps = (tetri: number, bps: number): number => {
    const scaled = (tetri * bps) / 10_000;
    return scaled < 0 ? -Math.round(-scaled) : Math.round(scaled);
};

/** The period an ISO `YYYY-MM-DD` date falls in. */
export const periodOfDate = (isoDate: string): PayrollPeriod =>
    isoDate.slice(0, 7);

const yearOf = (period: PayrollPeriod): number => Number(period.slice(0, 4));
const monthOf = (period: PayrollPeriod): number => Number(period.slice(5, 7));

/** Calendar days in a `YYYY-MM` period. Day 0 of the next month, so no table. */
export const daysInPeriod = (period: PayrollPeriod): number =>
    new Date(Date.UTC(yearOf(period), monthOf(period), 0)).getUTCDate();

/** First calendar day of a period, ISO `YYYY-MM-DD`. */
export const periodStartDate = (period: PayrollPeriod): string =>
    `${period}-01`;

/** Last calendar day of a period, ISO `YYYY-MM-DD` (a run's entry date). */
export const periodEndDate = (period: PayrollPeriod): string =>
    `${period}-${daysInPeriod(period).toString().padStart(2, '0')}`;

/**
 * The statutory filing and payment deadline for a period: the **15th of the
 * following month**. Informational — nothing here files anything.
 */
export const filingDueDate = (period: PayrollPeriod): string => {
    const month = monthOf(period);
    const rollsOver = month === 12;
    const year = rollsOver ? yearOf(period) + 1 : yearOf(period);
    const nextMonth = rollsOver ? 1 : month + 1;
    return `${year}-${nextMonth.toString().padStart(2, '0')}-15`;
};

/** Day-of-month of an ISO date, or `null` when it is not in `period`. */
const dayWithin = (isoDate: string, period: PayrollPeriod): number | null =>
    isoDate.slice(0, 7) === period ? Number(isoDate.slice(8, 10)) : null;

// ── employment days ─────────────────────────────────────────────────────────

/** What the day count needs to know about an employment. */
export type EmploymentSpan = {
    /** ISO `YYYY-MM-DD`. */
    hireDate: string;
    /** ISO `YYYY-MM-DD`; null while still employed. */
    terminationDate: string | null;
};

/**
 * Calendar days of `span` that fall inside `period`, **both ends inclusive**.
 *
 * The hire day and the termination day are both worked days, so an employee
 * hired on the 1st and terminated on the 31st of a 31-day month has 31 days, not
 * 30. Zero when the employment does not overlap the period at all — the caller
 * pairs that with a reason so the row is shown and explained rather than dropped.
 */
export const daysEmployedInPeriod = (
    span: EmploymentSpan,
    period: PayrollPeriod,
): number => {
    const total = daysInPeriod(period);
    const start = periodStartDate(period);
    const end = periodEndDate(period);

    if (span.hireDate > end) {
        return 0;
    }
    if (span.terminationDate !== null && span.terminationDate < start) {
        return 0;
    }

    const firstDay =
        span.hireDate <= start ? 1 : (dayWithin(span.hireDate, period) ?? 1);
    const lastDay =
        span.terminationDate === null || span.terminationDate >= end
            ? total
            : (dayWithin(span.terminationDate, period) ?? total);

    return Math.max(0, lastDay - firstDay + 1);
};

// ── the per-employee computation ────────────────────────────────────────────

/** What the calculation needs from an employee (nothing else). */
export type PayrollInput = EmploymentSpan & {
    /** Contractual **monthly** gross, in tetri. */
    grossSalaryTetri: number;
    /** When false, no pension is calculated on either side. */
    pensionParticipant: boolean;
};

/** One employee's amounts for one period, all integer tetri. */
export type PayrollComputation = {
    daysEmployed: number;
    daysInPeriod: number;
    isProrated: boolean;
    /** The gross actually charged (prorated where applicable). */
    grossTetri: number;
    incomeTaxTetri: number;
    employeePensionTetri: number;
    employerPensionTetri: number;
    /** Derived: `gross − incomeTax − employeePension`. */
    netTetri: number;
    /** **Never journalized.** Information only — see the module note. */
    stateContributionTetri: number;
    reason: PayrollLineReason;
};

/**
 * Compute one employee's payroll for one period.
 *
 * A non-participant gets **no pension on either side** — not a zeroed line, no
 * line at all in the entry — while tax and net still compute normally.
 */
export const computePayroll = (
    input: PayrollInput,
    period: PayrollPeriod,
    rates: PayrollRates = PAYROLL_RATES,
): PayrollComputation => {
    const total = daysInPeriod(period);
    const daysEmployed = daysEmployedInPeriod(input, period);

    const nil = (reason: PayrollLineReason): PayrollComputation => ({
        daysEmployed,
        daysInPeriod: total,
        isProrated: false,
        grossTetri: 0,
        incomeTaxTetri: 0,
        employeePensionTetri: 0,
        employerPensionTetri: 0,
        netTetri: 0,
        stateContributionTetri: 0,
        reason,
    });

    if (daysEmployed <= 0) {
        // Both ends of "not in this period" are distinguished, because "hired in
        // August" and "left in April" are different facts about a June run.
        return nil(
            input.hireDate > periodEndDate(period)
                ? 'NOT_YET_HIRED'
                : 'ALREADY_LEFT',
        );
    }
    if (input.grossSalaryTetri <= 0) {
        return nil('ZERO_SALARY');
    }

    const isProrated = daysEmployed < total;
    // Rounded once, here. Everything downstream is a percentage of *this* value.
    const grossTetri = isProrated
        ? Math.round((input.grossSalaryTetri * daysEmployed) / total)
        : input.grossSalaryTetri;

    const incomeTaxTetri = applyBps(grossTetri, rates.incomeTaxBps);
    const employeePensionTetri = input.pensionParticipant
        ? applyBps(grossTetri, rates.employeePensionBps)
        : 0;
    const employerPensionTetri = input.pensionParticipant
        ? applyBps(grossTetri, rates.employerPensionBps)
        : 0;
    const stateContributionTetri = input.pensionParticipant
        ? applyBps(grossTetri, rates.stateContributionBps)
        : 0;

    return {
        daysEmployed,
        daysInPeriod: total,
        isProrated,
        grossTetri,
        incomeTaxTetri,
        employeePensionTetri,
        employerPensionTetri,
        // Derived from the rounded components — never `78% of gross`. This is
        // what makes the line balance to the tetri.
        netTetri: grossTetri - incomeTaxTetri - employeePensionTetri,
        stateContributionTetri,
        reason: isProrated ? 'PRORATED' : 'OK',
    };
};

/**
 * The employer's own monthly pension charge for an employee at their contractual
 * gross — the register summary's "employer cost = gross + employer 2%" figure.
 * No proration: this is a forward-looking monthly cost, not a period's charge.
 */
export const monthlyEmployerPensionTetri = (
    grossSalaryTetri: number,
    pensionParticipant: boolean,
    rates: PayrollRates = PAYROLL_RATES,
): number =>
    pensionParticipant
        ? applyBps(grossSalaryTetri, rates.employerPensionBps)
        : 0;
